import { SlotButton } from '@avanta/shared/components/SlotButton';
import { Calendar } from '@avanta/shared/components/ui/Calendar';
import { FLOW_MODES } from '@avanta/shared/constants/appointments';
import { APPOINTMENTS_SCHEDULE_DAYS, LOCIZE_NS } from '@avanta/shared/constants/common';
import {
  checkIsDayDisabled,
  getFirstAvailableDate,
  getTimeSlotsForDate,
} from '@avanta/shared/utils/appointments';
import { formatDateTimeUnified, extractTimeFromUnified } from '@avanta/shared/utils/date';
import { addDays, isSameMonth, startOfDay } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AppointmentsReservePayload } from 'pages/AppointmentsReserve/types';
import { StepSection } from '../../../ui/StepSection';
import { useFetchFreeHours } from '../hooks/useFetchFreeHours';

export const AppointmentsCalendar = (): React.ReactElement => {
  const { setValue, watch } = useFormContext();
  const { availableDates } = useFetchFreeHours();
  const today = useMemo(() => startOfDay(new Date()), []);
  const endDate = useMemo(() => startOfDay(addDays(today, APPOINTMENTS_SCHEDULE_DAYS)), [today]);
  const [prevAvailableDates, setPrevAvailableDates] = useState(availableDates);
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    getFirstAvailableDate(availableDates)
  );

  if (prevAvailableDates !== availableDates) {
    setPrevAvailableDates(availableDates);
    if (!selectedDate) {
      setSelectedDate(getFirstAvailableDate(availableDates));
    }
  }

  const [dateTime, flow] = watch([
    AppointmentsReservePayload.dateTime,
    AppointmentsReservePayload.flow,
  ]);
  const selectedTime = dateTime ? extractTimeFromUnified(dateTime) : '';

  const allAvailableSlots = useMemo(() => {
    if (!selectedDate) return [];
    return getTimeSlotsForDate(selectedDate, availableDates).allAvailableSlots;
  }, [selectedDate, availableDates]);

  const handleOnSelectTime = (time: string): void => {
    const isDeselecting = time === selectedTime;
    if (flow !== FLOW_MODES.edit && flow !== FLOW_MODES.search) {
      setValue(AppointmentsReservePayload.psychologist, undefined);
    }
    setValue(
      AppointmentsReservePayload.dateTime,
      !isDeselecting && selectedDate ? formatDateTimeUnified(selectedDate, time) : ''
    );
  };

  const isDayDisabled = useCallback(
    (day: Date): boolean => checkIsDayDisabled(day, availableDates, today, endDate),
    [availableDates, today, endDate]
  );

  const handleOnSelectDay = (date: Date): void => {
    setSelectedDate(date);
    setValue(AppointmentsReservePayload.dateTime, '');
  };

  return (
    <StepSection
      title={t('AppointmentsReserve.Steps.session.dateTime.title')}
      className="grid am-lg:grid-cols-2 gap-4 am-lg:gap-12 p-0 am-lg:p-6 border border-transparent am-lg:border-teal-300 rounded-xl h-full"
      containerClassName="h-full am-lg:gap-2"
    >
      <Calendar
        className="w-full border border-teal-300 am-lg:border-transparent rounded-xl px-2 am-lg:p-0"
        selected={selectedDate}
        defaultMonth={selectedDate}
        startMonth={today}
        endMonth={endDate}
        onSelect={handleOnSelectDay}
        disabled={isDayDisabled}
        showOutsideDays
        {...(isSameMonth(today, endDate) && { hideNavigation: true })}
      />
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-denim-500">
          {t('AppointmentsReserve.Steps.session.dateTime.time.label')}
        </p>
        <div className="flex am-lg:flex-col gap-3.5 am-lg:gap-4 flex-col justify-between h-full">
          {!selectedDate && (
            <p className="am-lg:text-sm text-xs font-normal leading-4 text-gray-500">
              {t('AppointmentsReserve.Steps.session.dateTime.emptyDay')}
            </p>
          )}
          <div className="grid grid-cols-5 gap-2">
            {allAvailableSlots.map(({ time, disabled }) => (
              <SlotButton
                key={time}
                isActive={selectedTime === time}
                disabled={disabled}
                onClick={() => handleOnSelectTime(time)}
              >
                {time}
              </SlotButton>
            ))}
          </div>
        </div>
      </div>
    </StepSection>
  );
};
